import { hasObjectKey } from '@core/utils';
import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneDeep, debounce } from 'es-toolkit';
import { useMemo } from 'react';

import { AuthApi, AuthQueryKey } from '@/api';
import { ROUTES } from '@/constants';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { isSameEndpointQuery } from '@/utils/isSameEndpointQuery';

import { useCustomRouter } from './useCustomRouter';

interface LikeParams {
  /**
   * 쿼리키 조회용
   */
  idx?: string;
  contentId: number;
  isLike: boolean;
  likeType: string;
}

const OPTIMISTIC_QUERY_KEYS = [
  AuthQueryKey['get:/wello/v2/auth/wello-comment'],
  AuthQueryKey['get:/wello/v2/auth/wello-comment/my'],
];

const ID_KEYS = ['communityId', 'welloCommentId', 'id'] as const;

const findCommentById = (obj: unknown, contentId: number): unknown => {
  if (!obj || typeof obj !== 'object') return undefined;

  for (const idKey of ID_KEYS) {
    if (hasObjectKey(obj, idKey) && obj[idKey] === contentId) {
      return obj;
    }
  }

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = findCommentById(item, contentId);
      if (found) return found;
    }
  } else {
    for (const key in obj) {
      if (hasObjectKey(obj, key)) {
        const found = findCommentById(obj[key], contentId);
        if (found) return found;
      }
    }
  }

  return undefined;
};

const updateLikeState = (comment: unknown, isLike: boolean) => {
  if (!comment || typeof comment !== 'object') return;

  if (
    hasObjectKey(comment, 'likeCount') &&
    typeof comment.likeCount === 'number'
  ) {
    comment.likeCount = isLike
      ? comment.likeCount - 1
      : comment.likeCount + 1;
  }

  if (
    hasObjectKey(comment, 'likeYN') &&
    typeof comment.likeYN === 'boolean'
  ) {
    comment.likeYN = !isLike;
  }
};

const fixInfiniteQueryKey = (key: QueryKey): QueryKey => {
  const fixed = cloneDeep(key) as unknown[];
  if (
    fixed[0] &&
    typeof fixed[0] === 'object' &&
    hasObjectKey(fixed[0], 'query') &&
    typeof fixed[0].query === 'object' &&
    hasObjectKey(fixed[0].query, 'page')
  ) {
    fixed[0].query.page = 1;
  }

  return fixed;
};

export const useLike = () => {
  const { mutate } = useMutation({
    mutationFn: ({ contentId, isLike, likeType }: LikeParams) => {
      const request = isLike
        ? AuthApi['delete:/wello/v2/auth/wello-like']
        : AuthApi['post:/wello/v2/auth/wello-like'];

      return request({
        body: {
          contentId: contentId,
          likeTypeCd: likeType,
        },
      });
    },
  });

  const queryClient = useQueryClient();

  const router = useCustomRouter();

  const { request: requestHaptic } = useHandleApp({
    type: MESSAGE_KEYWORD.HAPTIC,
  });

  const like = useMemo(() => {
    const backupMap = new Map<QueryKey, unknown>();

    const originLikeMap: Map<number, boolean> = new Map();

    const debouncedMutateMap: Map<
      number,
      ReturnType<typeof debounce>
    > = new Map([]);

    return (params: LikeParams) => {
      requestHaptic();

      const { contentId, isLike, idx } = params;

      const { isLogin } = useAuthStore.getState();

      if (!isLogin)
        return router.push(
          ROUTES.LOGIN.withSearchParams({
            searchParams: {
              redirect: location.href,
            },
          }),
        );

      if (!originLikeMap.has(contentId)) originLikeMap.set(contentId, isLike);

      const likeQueries = queryClient.getQueriesData({
        predicate: (query) =>
          OPTIMISTIC_QUERY_KEYS.some((queryKey) =>
            isSameEndpointQuery(query, queryKey()),
          ),
      });

      likeQueries.forEach(([key, data]) => {
        if (
          (key.includes(contentId) || (idx && key.includes(idx))) &&
          hasObjectKey(data, 'context') &&
          hasObjectKey(data.context, 'likeCount') &&
          hasObjectKey(data.context, 'likeYN')
        ) {
          const newData = cloneDeep(data);

          if (
            hasObjectKey(newData.context, 'likeCount') &&
            typeof newData.context.likeCount === 'number' &&
            hasObjectKey(newData.context, 'likeYN') &&
            typeof newData.context.likeYN === 'boolean'
          ) {
            newData.context.likeCount = isLike
              ? newData.context.likeCount - 1
              : newData.context.likeCount + 1;

            newData.context.likeYN = !isLike;

            queryClient.setQueryData(key, newData);
          }
        } else {
          if (!backupMap.has(key)) {
            backupMap.set(key, cloneDeep(data));
          }

          if (!data) return;

          const newData = cloneDeep(data);
          const comment = findCommentById(newData, contentId);

          if (comment) {
            updateLikeState(comment, isLike);
            queryClient.setQueryData(fixInfiniteQueryKey(key), newData);
          }
        }
      });

      if (!debouncedMutateMap.has(contentId)) {
        debouncedMutateMap.set(
          contentId,
          debounce<typeof mutate>((params, options) => {
            if (originLikeMap.get(contentId) === !params.isLike) return;

            return mutate(params, options);
          }, 500),
        );
      }

      debouncedMutateMap.get(contentId)?.(params, {
        onError: () => {
          originLikeMap.delete(contentId);
          backupMap.forEach((data, key) => {
            queryClient.setQueryData(key, data);
          });
        },
        onSuccess: () => {
          backupMap.clear();

          originLikeMap.set(contentId, !params.isLike);
        },
      });
    };
  }, [mutate, queryClient, requestHaptic, router]);

  return { like };
};
